import React from 'react';
import { motion } from 'framer-motion';
import './About.css';

const About: React.FC = () => {
  const stats = [
    { number: '15+', label: 'Projects Completed (Building & Event)' },
    { number: '3+', label: 'Years Experience' },
    { number: '10K+', label: 'Total Community Members Managed' },
    { number: '8', label: 'Ambassador & Leadership Roles' }
  ];

  const timeline = [
    {
      year: '2023',
      title: 'Blockchain Development Journey Begins',
      description: 'Started at CookieLabs as BE & Blockchain Developer. Designed and developed Jetton tokenomics for Gatekeeper project using TON blockchain, Tact, and TypeScript.'
    },
    {
      year: '2024',
      title: 'Community Leadership & Education',
      description: 'Became TON Society Korea Moderator managing 7K+ members. Organized KBW2024 events, meme competitions, and AMAs. Started teaching blockchain & Solidity at Class101.'
    },
    {
      year: '2025',
      title: 'AI & Enterprise Development',
      description: 'Joined Kakao Enterprise Academy as Backend Developer. Built RAG-based AI chatbot admin systems with OCR processing and document management capabilities.'
    },
    {
      year: '2025',
      title: 'International Research & Ambassadorships',
      description: 'Research Assistant at UNLV AI Labs (U.S.) working on U-Net model fine-tuning. Active as PEAQ, DeNet, Teneo, Acurast, and OpenXAI Ambassador across multiple ecosystems.'
    }
  ];

  return (
    <div className="about">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <motion.div
            className="about-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="about-text">
              <h1>About Me</h1>
              <p className="lead">
                I'm Andy Lee (Tae ho Lee), a blockchain and full-stack developer specializing in React, TypeScript, 
                and blockchain development. I'm passionate about building community platforms and AI-powered solutions.
              </p>
              <p>
                I have been actively involved in various activities such as community building, presentations, 
                writing articles, and education. I prioritize designs that consider both user experience (UX) 
                and developer experience (DX). Additionally, I have over two years of backend project experience 
                utilizing Java and Spring, with expertise in building AI chatbots and community platforms.
              </p>
            </div>
            <div className="about-image">
              <img 
                src={process.env.PUBLIC_URL + '/images/profile.jpg'} 
                alt="이태호"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <motion.div
            className="stats-grid"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="stat-item"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <h3>{stat.number}</h3>
                <p>{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="skills-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2>Skills & Expertise</h2>
            <div className="skills-categories">
              <div className="skill-category">
                <h3>Blockchain</h3>
                <div className="skill-tags">
                  <span>Solidity</span>
                  <span>Smart Contracts</span>
                  <span>DeFi</span>
                  <span>DePIN</span>
                  <span>Research</span>
                  <span>event operation & Community</span>
                </div>
              </div>
              <div className="skill-category">
                <h3>Backend</h3>
                <div className="skill-tags">
                  <span>Java</span>
                  <span>Kotlin</span>
                  <span>Spring Boot</span>
                  <span>OracleDB</span>
                  <span>kakao Cloud</span>
                  <span>AWS</span>
                  <span>Docker</span>
                  <span>ELK Stack</span>
                </div>
              </div>
              <div className="skill-category">
                <h3>Frontend & Tools</h3>
                <div className="skill-tags">
                  <span>React</span>
                  <span>TypeScript</span>
                  <span>VSC, IntelliJ</span>
                  <span>Kubernetes</span>
                  <span>GitHub</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="timeline-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2>My Journey</h2>
            <div className="timeline">
              {timeline.map((item, index) => (
                <motion.div
                  key={index}
                  className="timeline-item"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="timeline-year">{item.year}</div>
                  <div className="timeline-content">
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Awards & Achievements Section */}
      <section className="awards-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2>Awards</h2>
            <div className="awards-grid">
              <motion.div
                className="award-item"
                whileHover={{ y: -5 }}
                transition={{ duration: 0.2 }}
              >
                <div className="award-icon">🏆</div>
                <h3>Impact Award (3rd Place)</h3>
                <p>WFUNA Global Impact Hackathon</p>
                <span className="award-detail">62 participants, 17 universities - 1M KRW prize</span>
              </motion.div>
              <motion.div
                className="award-item"
                whileHover={{ y: -5 }}
                transition={{ duration: 0.2 }}
              >
                <div className="award-icon">🥇</div>
                <h3>Excellence Award</h3>
                <p>AI & Blockchain Startup Competition</p>
                <span className="award-detail">"Silta" - AI copyright protection platform using IPFS & blockchain</span>
              </motion.div>
              <motion.div
                className="award-item"
                whileHover={{ y: -5 }}
                transition={{ duration: 0.2 }}
              >
                <div className="award-icon">🎯</div>
                <h3>Winner of Chainlink Track</h3>
                <p>2nd Place in Monad Blitz</p>
                <span className="award-detail">Developed NAD.bet leveraging @chainlink services and CCIP</span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Community & Education Section */}
      <section className="community-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2>Community & Education</h2>
            <div className="community-grid">
              <div className="community-category">
                <h3>🎓 Education & Teaching</h3>
                <div className="community-list">
                  <div className="community-item">
                    <h4>Blockchain & Solidity Instructor</h4>
                    <p>Class101 - Currently filming educational videos for blockchain track</p>
                  </div>
                  <div className="community-item">
                    <h4>Gairos Regular Sessions</h4>
                    <p>Led blockchain society sessions for Terms 3-4 as president</p>
                  </div>
                  <div className="community-item">
                    <h4>GDGOC Gachon Organizer</h4>
                    <p>Organized Spring study group focused on Kotlin development and TDD</p>
                  </div>
                </div>
              </div>
              <div className="community-category">
                <h3>🌐 Ambassador Roles</h3>
                <div className="community-list">
                  <div className="community-item">
                    <h4>TON Society Korea Moderator</h4>
                    <p>Managing 7K+ Telegram community, organizing events and AMAs</p>
                  </div>
                  <div className="community-item">
                    <h4>PEAQ Ambassador</h4>
                    <p>Founded PEAQ Korea community, planning first Korea meetup (100+ participants)</p>
                  </div>
                  <div className="community-item">
                    <h4>Multi-Ecosystem Ambassador</h4>
                    <p>DeNet, Teneo, Acurast, OpenXAI - Research, KOL meetings, seminar organization</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Presentations Section */}
      <section className="presentations-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2>Presentations & Talks</h2>
            <div className="presentations-list">
              <motion.div
                className="presentation-item"
                whileHover={{ x: 10 }}
                transition={{ duration: 0.2 }}
              >
                <div className="presentation-date">Oct 2, 2024</div>
                <div className="presentation-content">
                  <h3>"The Need for Community Building and Why It's Okay to Fail"</h3>
                  <p>GDG Gachon Regular Session</p>
                </div>
              </motion.div>
              <motion.div
                className="presentation-item"
                whileHover={{ x: 10 }}
                transition={{ duration: 0.2 }}
              >
                <div className="presentation-date">May 23, 2025</div>
                <div className="presentation-content">
                  <h3>STORY PROTOCOL University Local Seminar</h3>
                  <p>Architecture overview and live coding demonstration</p>
                </div>
              </motion.div>
              <motion.div
                className="presentation-item"
                whileHover={{ x: 10 }}
                transition={{ duration: 0.2 }}
              >
                <div className="presentation-date">Jun 20, 2025</div>
                <div className="presentation-content">
                  <h3>ACURAST Korea Local Seminar</h3>
                  <p>Presentation on ACURAST protocol and DePIN</p>
                </div>
              </motion.div>
              <motion.div
                className="presentation-item"
                whileHover={{ x: 10 }}
                transition={{ duration: 0.2 }}
              >
                <div className="presentation-date">Jul 25, 2025</div>
                <div className="presentation-content">
                  <h3>PEAQ Korea Local Seminar</h3>
                  <p>Presentation on DePIN and PEAQ ecosystem</p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;